import FormAlert from "./FormAlert";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { checkAuthorization } from "@/common/auth";
import { usePatchAdminUserMutation } from "@/api/api";
import { getApiErrorDetail } from "@/api/errors";
import { HGAdminUser, HGAdminUserPatch } from "@/common/types";
import { Button } from "../button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../dialog";
import { Field, FieldContent, FieldDescription, FieldLabel } from "../field";
import { Input } from "../input";
import { Switch } from "../switch";

interface FormValues {
  is_admin: boolean;
  enabled: boolean;
  is_special: boolean;
  records_exempt: boolean;
  collection_slots: string;
  pinned_slots: string;
  builtin_rank: string;
}

// The API's bounds for every numeric field here (hg/schemas/admin.py).
const MAX_SLOTS = 1000;

const toValues = (user: HGAdminUser): FormValues => ({
  is_admin: user.is_admin,
  enabled: !user.disabled,
  is_special: user.is_special,
  records_exempt: user.records_exempt,
  collection_slots: String(user.collection_slots),
  pinned_slots: String(user.pinned_slots),
  builtin_rank: user.builtin_rank == null ? "" : String(user.builtin_rank),
});

const parseCount = (value: string): number | null => {
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const n = parseInt(trimmed, 10);
  return n <= MAX_SLOTS ? n : null;
};

/**
 * Every admin-managed setting of one account, saved as a single patch.
 *
 * Only the fields that changed are sent, so saving with nothing touched
 * writes nothing, and an admin opening their own row cannot trip the API's
 * self-demotion checks with values they never changed.
 */
export default function AdminUserEditDialog({
  open,
  onOpenChange,
  user,
  isSelf,
  name,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: HGAdminUser;
  isSelf: boolean;
  name: string;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        {/* Mounted only while open, so the form seeds from the row as it is
            now rather than as it was when the page loaded. */}
        <EditForm
          user={user}
          isSelf={isSelf}
          name={name}
          onDone={() => onOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  );
}

function EditForm({
  user,
  isSelf,
  name,
  onDone,
}: {
  user: HGAdminUser;
  isSelf: boolean;
  name: string;
  onDone: () => void;
}) {
  const [patch, { isLoading }] = usePatchAdminUserMutation();
  const [error, setError] = useState<string | null>(null);
  const form = useForm<FormValues>({ defaultValues: toValues(user) });

  const onSubmit = async (values: FormValues) => {
    setError(null);
    const collectionSlots = parseCount(values.collection_slots);
    const pinnedSlots = parseCount(values.pinned_slots);
    if (collectionSlots === null || pinnedSlots === null) {
      setError(`Slots must be whole numbers from 0 to ${MAX_SLOTS}.`);
      return;
    }
    // Blank is a value here, not a missing one: it drops the account out of
    // the built-in list.
    const rankInput = values.builtin_rank.trim();
    const builtinRank = rankInput === "" ? null : parseCount(rankInput);
    if (rankInput !== "" && builtinRank === null) {
      setError(
        `Built-in position must be a whole number from 0 to ${MAX_SLOTS}, or blank.`,
      );
      return;
    }

    const fields: HGAdminUserPatch = {};
    if (values.is_admin !== user.is_admin) fields.is_admin = values.is_admin;
    if (values.enabled === user.disabled) fields.disabled = !values.enabled;
    if (values.is_special !== user.is_special)
      fields.is_special = values.is_special;
    if (values.records_exempt !== user.records_exempt)
      fields.records_exempt = values.records_exempt;
    if (collectionSlots !== user.collection_slots)
      fields.collection_slots = collectionSlots;
    if (pinnedSlots !== user.pinned_slots) fields.pinned_slots = pinnedSlots;
    if (builtinRank !== user.builtin_rank) fields.builtin_rank = builtinRank;

    if (Object.keys(fields).length === 0) {
      onDone();
      return;
    }
    try {
      await patch({ userID: user.id, patch: fields }).unwrap();
      if (isSelf) await checkAuthorization();
      onDone();
    } catch (e) {
      setError(
        getApiErrorDetail(e, "Could not update that user. Please try again."),
      );
    }
  };

  const switchField = (
    field: "is_admin" | "enabled" | "is_special" | "records_exempt",
    label: string,
    description: string,
    disabled = false,
  ) => (
    <Controller
      name={field}
      control={form.control}
      render={({ field: { value, onChange } }) => (
        <Field orientation="horizontal" data-disabled={disabled}>
          <FieldContent>
            <FieldLabel htmlFor={`edit-${field}`}>{label}</FieldLabel>
            <FieldDescription>{description}</FieldDescription>
          </FieldContent>
          <Switch
            id={`edit-${field}`}
            checked={value}
            disabled={disabled}
            onCheckedChange={onChange}
          />
        </Field>
      )}
    />
  );

  const numberField = (
    field: "collection_slots" | "pinned_slots" | "builtin_rank",
    label: string,
    placeholder?: string,
  ) => (
    <Controller
      name={field}
      control={form.control}
      render={({ field: props }) => (
        <Field>
          <FieldLabel htmlFor={`edit-${field}`}>{label}</FieldLabel>
          <Input
            {...props}
            id={`edit-${field}`}
            inputMode="numeric"
            autoComplete="off"
            placeholder={placeholder}
          />
        </Field>
      )}
    />
  );

  return (
    // Inside DialogContent, not around it -- see AdminUserCreateDialog.
    <form onSubmit={form.handleSubmit(onSubmit)}>
      <DialogHeader>
        <DialogTitle>Edit {name}</DialogTitle>
        <DialogDescription>
          Saved together; only what you change is written.
        </DialogDescription>
      </DialogHeader>
      <div className="flex flex-col gap-4 py-2">
        {switchField(
          "enabled",
          "Enabled",
          isSelf
            ? "You cannot disable your own account."
            : "Disabling signs the account out everywhere and blocks every login method.",
          isSelf,
        )}
        {switchField(
          "is_admin",
          "Admin",
          isSelf
            ? "You cannot remove your own admin access."
            : "Full access to this page and every account.",
          isSelf,
        )}
        {switchField(
          "is_special",
          "Special",
          "A mark for accounts that are not people. Only this page reads it.",
        )}
        {switchField(
          "records_exempt",
          "Exempt from limits",
          "Ignores every quota and volume cap, without admin access.",
        )}
        <div className="grid grid-cols-3 gap-3">
          {numberField("collection_slots", "Collection slots")}
          {numberField("pinned_slots", "Pinned slots")}
          {numberField("builtin_rank", "Built-in position", "Not listed")}
        </div>
        <p className="text-xs text-muted-foreground -mt-2">
          Built-in position places the account in the switcher a signed-out
          visitor sees, lowest first. Leave it blank to keep it out.
        </p>
        <FormAlert error={error} />
      </div>
      <DialogFooter>
        <DialogClose asChild>
          <Button variant="outline" type="button">
            Cancel
          </Button>
        </DialogClose>
        <Button type="submit" disabled={isLoading}>
          Save
        </Button>
      </DialogFooter>
    </form>
  );
}
